import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { toast } from 'sonner';
import { Heart, Check } from 'lucide-react';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';
import axios from 'axios';

const donationAmounts = [25, 50, 100, 250, 500, 1000];
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const Donate = () => {
  const [donationType, setDonationType] = useState('one-time');
  const [amount, setAmount] = useState('');
  const [customAmount, setCustomAmount] = useState('');
  const [showPayPal, setShowPayPal] = useState(false);
  const [donationId, setDonationId] = useState(null);
  const [publicDonations, setPublicDonations] = useState([]);
  const [donorInfo, setDonorInfo] = useState({
    firstName: '',
    lastName: '',
    email: '',
    companyName: '',
    isNonProfit: false,
    message: '',
    anonymous: false
  });

  // Fetch public donations on mount
  React.useEffect(() => {
    fetchPublicDonations();
  }, []);

  const fetchPublicDonations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/donations/public`);
      setPublicDonations(response.data);
    } catch (error) {
      console.error('Error fetching public donations:', error);
    }
  };

  const handleAmountSelect = (value) => {
    setAmount(value.toString());
    setCustomAmount('');
  };

  const handleCustomAmount = (e) => {
    setCustomAmount(e.target.value);
    setAmount('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDonorInfo(prev => ({ 
      ...prev, 
      [name]: type === 'checkbox' ? checked : value 
    }));
  };

  const handleProceedToPayment = (e) => {
    e.preventDefault();
    
    const finalAmount = customAmount || amount;
    if (!finalAmount || parseFloat(finalAmount) <= 0) {
      toast.error('Please enter a valid donation amount.');
      return;
    }

    if (!donorInfo.firstName || !donorInfo.lastName || !donorInfo.email) {
      toast.error('Please fill in all required fields.');
      return;
    }

    // Show PayPal button
    setShowPayPal(true);
    toast.info('Please complete your donation using PayPal below.');
  };

  // PayPal initial options
  const paypalClientId = process.env.REACT_APP_PAYPAL_CLIENT_ID;
  
  // Debug log
  React.useEffect(() => {
    console.log('PayPal Client ID:', paypalClientId ? 'Loaded' : 'Missing');
  }, [paypalClientId]);

  const initialOptions = {
    clientId: paypalClientId,
    currency: 'USD',
    intent: donationType === 'monthly' ? 'subscription' : 'capture',
  };

  const createOrder = async (data, actions) => {
    try {
      const finalAmount = customAmount || amount;
      
      console.log('Creating order:', { finalAmount, donationType });
      
      // Create order in backend
      const response = await axios.post(`${BACKEND_URL}/api/donations/create-order`, {
        donation: {
          ...donorInfo,
          amount: parseFloat(finalAmount),
          donationType
        }
      });

      console.log('Order created:', response.data);
      setDonationId(response.data.donationId);
      return response.data.orderId;
    } catch (error) {
      console.error('Error creating order:', error);
      console.error('Error details:', error.response?.data);
      toast.error('Failed to create donation order. Please try again.');
      throw error;
    }
  };

  const onApprove = async (data, actions) => {
    try {
      // Capture the order in backend
      const response = await axios.post(`${BACKEND_URL}/api/donations/capture-order`, {
        orderId: data.orderID,
        donationId: donationId
      });

      toast.success('Thank you for your donation! A confirmation email has been sent.');
      
      // Refresh public donations list
      fetchPublicDonations();
      
      // Reset form
      setAmount('');
      setCustomAmount('');
      setShowPayPal(false);
      setDonorInfo({
        firstName: '',
        lastName: '',
        email: '',
        companyName: '',
        isNonProfit: false,
        message: '',
        anonymous: false
      });
      
      // Scroll to top to see updated donations
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      console.error('Error capturing order:', error);
      toast.error('Payment failed. Please try again.');
    }
  };

  const onError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment failed. Please try again.');
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-400 to-blue-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <Heart className="w-16 h-16 mx-auto mb-6" />
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Make a</span>{' '}
            <span className="font-black">Difference</span>
          </h1>
          <p className="text-xl max-w-3xl mx-auto">
            Your generous donation helps us provide mentorship, resources, and opportunities to deserving young individuals.
          </p>
        </div>
      </section>

      {/* Impact Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Your Impact</h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-cyan-600 mb-2">$25</div>
                <p className="text-gray-700">Provides workshop materials for one Cub</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-blue-600 mb-2">$100</div>
                <p className="text-gray-700">Supports one month of mentorship program</p>
              </CardContent>
            </Card>
            <Card className="text-center">
              <CardContent className="p-6">
                <div className="text-4xl font-bold text-purple-600 mb-2">$500</div>
                <p className="text-gray-700">Funds a scholarship for a deserving student</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Recent Donations Section */}
      {publicDonations.length > 0 && (
        <section className="py-12 bg-gradient-to-br from-cyan-50 to-blue-50 overflow-hidden">
          <div className="container mx-auto px-4">
            <div className="text-center mb-10">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-gray-900">
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontWeight: '400' }}>Recent</span>{' '}
                <span className="font-black bg-gradient-to-r from-cyan-600 to-blue-600 bg-clip-text text-transparent">Donors</span>
              </h2>
              <p className="text-gray-600 text-lg">
                Join these generous supporters in making a difference
              </p>
            </div>

            {/* Scrolling Donations Container */}
            <div className="relative">
              <div className="flex animate-scroll-donations" style={{ animation: 'scroll-donations 40s linear infinite' }}>
                {/* First set of donations */}
                {publicDonations.map((donation, index) => {
                  // Determine display name
                  let displayName;
                  if (donation.anonymous) {
                    displayName = "Generous supporter";
                  } else if (donation.companyName) {
                    displayName = donation.companyName;
                  } else {
                    // Show "FirstName L." format
                    const lastInitial = donation.lastName ? donation.lastName.charAt(0).toUpperCase() + '.' : '';
                    displayName = `${donation.firstName} ${lastInitial}`;
                  }

                  return (
                    <Card key={`first-${index}`} className="flex-shrink-0 w-80 mx-4 hover:shadow-lg transition-shadow bg-white border-l-4 border-cyan-500">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                              <h3 className="font-bold text-gray-900 text-sm">
                                {displayName}
                              </h3>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-green-600">
                              ${donation.amount.toFixed(0)}
                            </div>
                            {donation.donationType === 'monthly' && (
                              <span className="text-xs text-blue-600 font-semibold">/month</span>
                            )}
                          </div>
                        </div>
                        
                        {donation.message && !donation.anonymous && (
                          <p className="text-sm text-gray-700 italic line-clamp-2 border-l-2 border-gray-200 pl-3">
                            "{donation.message}"
                          </p>
                        )}
                        
                        <div className="mt-3 text-xs text-gray-500">
                          {new Date(donation.completedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
                
                {/* Duplicate set for seamless loop */}
                {publicDonations.map((donation, index) => {
                  let displayName;
                  if (donation.anonymous) {
                    displayName = "Generous supporter";
                  } else if (donation.companyName) {
                    displayName = donation.companyName;
                  } else {
                    const lastInitial = donation.lastName ? donation.lastName.charAt(0).toUpperCase() + '.' : '';
                    displayName = `${donation.firstName} ${lastInitial}`;
                  }

                  return (
                    <Card key={`second-${index}`} className="flex-shrink-0 w-80 mx-4 hover:shadow-lg transition-shadow bg-white border-l-4 border-cyan-500">
                      <CardContent className="p-6">
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Heart className="w-4 h-4 text-red-500 fill-red-500" />
                              <h3 className="font-bold text-gray-900 text-sm">
                                {displayName}
                              </h3>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-xl font-bold text-green-600">
                              ${donation.amount.toFixed(0)}
                            </div>
                            {donation.donationType === 'monthly' && (
                              <span className="text-xs text-blue-600 font-semibold">/month</span>
                            )}
                          </div>
                        </div>
                        
                        {donation.message && !donation.anonymous && (
                          <p className="text-sm text-gray-700 italic line-clamp-2 border-l-2 border-gray-200 pl-3">
                            "{donation.message}"
                          </p>
                        )}
                        
                        <div className="mt-3 text-xs text-gray-500">
                          {new Date(donation.completedAt).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Donation Form */}
      <section className="py-12 bg-gradient-to-br from-cyan-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <Card>
              <CardHeader>
                <CardTitle className="text-3xl text-center">Complete Your Donation</CardTitle>
                <CardDescription className="text-center">
                  All donations are tax-deductible. Lions Unleashed is a 501(c)(3) nonprofit organization.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleProceedToPayment} className="space-y-6">
                  {/* Donation Type */}
                  <div>
                    <Label className="text-lg mb-3 block">Donation Type</Label>
                    <RadioGroup value={donationType} onValueChange={setDonationType} className="grid grid-cols-2 gap-4">
                      <div>
                        <RadioGroupItem value="one-time" id="one-time" className="peer sr-only" />
                        <Label
                          htmlFor="one-time"
                          className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-200 bg-white p-4 hover:bg-gray-50 peer-data-[state=checked]:border-cyan-500 peer-data-[state=checked]:bg-cyan-50 cursor-pointer transition-all"
                        >
                          <span className="text-lg font-semibold">One-Time</span>
                        </Label>
                      </div>
                      <div>
                        <RadioGroupItem value="monthly" id="monthly" className="peer sr-only" />
                        <Label
                          htmlFor="monthly"
                          className="flex flex-col items-center justify-between rounded-lg border-2 border-gray-200 bg-white p-4 hover:bg-gray-50 peer-data-[state=checked]:border-cyan-500 peer-data-[state=checked]:bg-cyan-50 cursor-pointer transition-all"
                        >
                          <span className="text-lg font-semibold">Monthly</span>
                        </Label>
                      </div>
                    </RadioGroup>
                  </div>

                  {/* Preset Amounts */}
                  <div>
                    <Label className="text-lg mb-3 block">Select Amount</Label>
                    <div className="grid grid-cols-3 gap-4">
                      {donationAmounts.map((preset) => (
                        <Button
                          key={preset}
                          type="button"
                          variant={amount === preset.toString() ? 'default' : 'outline'}
                          className={`h-16 text-lg font-semibold ${
                            amount === preset.toString()
                              ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white'
                              : 'hover:border-cyan-500'
                          }`}
                          onClick={() => handleAmountSelect(preset)}
                        >
                          ${preset}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Custom Amount */}
                  <div>
                    <Label htmlFor="customAmount" className="text-lg">Or Enter Custom Amount</Label>
                    <div className="relative mt-2">
                      <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-lg">$</span>
                      <Input
                        id="customAmount"
                        type="number"
                        min="1"
                        step="0.01"
                        value={customAmount}
                        onChange={handleCustomAmount}
                        placeholder="Enter amount"
                        className="pl-8 text-lg h-12"
                      />
                    </div>
                  </div>

                  {/* Donor Information */}
                  <div className="space-y-4 pt-4 border-t">
                    <h3 className="text-xl font-semibold">Donor Information</h3>
                    
                    <div className="grid md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="firstName">First Name *</Label>
                        <Input
                          id="firstName"
                          name="firstName"
                          value={donorInfo.firstName}
                          onChange={handleInputChange}
                          required
                          disabled={showPayPal}
                        />
                      </div>
                      <div>
                        <Label htmlFor="lastName">Last Name *</Label>
                        <Input
                          id="lastName"
                          name="lastName"
                          value={donorInfo.lastName}
                          onChange={handleInputChange}
                          required
                          disabled={showPayPal}
                        />
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={donorInfo.email}
                        onChange={handleInputChange}
                        required
                        disabled={showPayPal}
                      />
                    </div>

                    <div>
                      <Label htmlFor="companyName">Company Name (Optional)</Label>
                      <Input
                        id="companyName"
                        name="companyName"
                        value={donorInfo.companyName}
                        onChange={handleInputChange}
                        disabled={showPayPal}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="isNonProfit"
                        name="isNonProfit"
                        checked={donorInfo.isNonProfit}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-600"
                        disabled={showPayPal}
                      />
                      <Label htmlFor="isNonProfit" className="text-sm">
                        This is a not-for-profit organization
                      </Label>
                    </div>

                    <div>
                      <Label htmlFor="message">Message (Optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={donorInfo.message}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Leave a message of support..."
                        disabled={showPayPal}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        id="anonymous"
                        name="anonymous"
                        checked={donorInfo.anonymous}
                        onChange={handleInputChange}
                        className="w-4 h-4 text-cyan-600"
                        disabled={showPayPal}
                      />
                      <Label htmlFor="anonymous" className="text-sm">
                        Make this donation anonymous
                      </Label>
                    </div>
                  </div>

                  {/* Submit Button or PayPal */}
                  {!showPayPal ? (
                    <Button
                      type="submit"
                      size="lg"
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white text-lg py-6 font-bold"
                    >
                      {donationType === 'monthly' ? 'Proceed to Monthly Donation' : 'Proceed to Donate'}
                    </Button>
                  ) : (
                    <div className="space-y-4">
                      <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
                        <p className="text-sm text-yellow-800">
                          Complete your ${customAmount || amount} {donationType} donation using PayPal below:
                        </p>
                      </div>
                      <PayPalScriptProvider options={initialOptions}>
                        <PayPalButtons
                          createOrder={createOrder}
                          onApprove={onApprove}
                          onError={onError}
                          style={{ layout: 'vertical' }}
                        />
                      </PayPalScriptProvider>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setShowPayPal(false)}
                        className="w-full"
                      >
                        Cancel / Edit Information
                      </Button>
                    </div>
                  )}

                  <p className="text-sm text-gray-600 text-center">
                    Secure payment processing powered by PayPal.
                  </p>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">Why Donate?</h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="flex items-start">
              <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Direct Impact</h3>
                <p className="text-gray-700">100% of your donation goes directly to program support, mentorship, and scholarships.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Tax Deductible</h3>
                <p className="text-gray-700">Lions Unleashed is a 501(c)(3) organization. Your donation is tax-deductible.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Change Lives</h3>
                <p className="text-gray-700">Help young professionals achieve their dreams through mentorship and resources.</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="w-8 h-8 bg-cyan-500 rounded-full flex items-center justify-center mr-4 flex-shrink-0 mt-1">
                <Check className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-bold text-lg mb-2">Recognition</h3>
                <p className="text-gray-700">Donors are recognized on our website (unless you choose to remain anonymous).</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Donate;
